/**
 * Pure calculation functions for the infinite carousel dock.
 * No side effects — all functions are deterministic given their inputs.
 */

/** Wrap offset into [0, count) range */
export function normalizeOffset(offset: number, count: number): number {
  return ((offset % count) + count) % count;
}

/**
 * Compute the signed relative position of item `index` from the current offset.
 * Result is in [-count/2, count/2) so the closest path around the ring is chosen.
 */
export function relativePosition(index: number, offset: number, count: number): number {
  const norm = normalizeOffset(offset, count);
  const half = count / 2;
  return ((((index - norm) % count) + count + half) % count) - half;
}

/** Y pixel coordinate for an item given its relative position */
export function itemY(relPos: number, spacing: number): number {
  return relPos * spacing;
}

/**
 * Estimated section block height (px) from item count.
 * Used as a fallback before the real DOM height is measured (SSR / first paint / tests).
 * Mirrors the CSS box of `sectionBlock`: padding 10px·2 + header(~17) + headerGap(8)
 * + N·dockButton(min 52) + (N-1)·itemGap(6).  →  39 + 58·N
 */
export function estimateSectionHeight(itemCount: number): number {
  const PADDING_Y = 10;
  const HEADER = 17;
  const HEADER_GAP = 8;
  const BUTTON_MIN = 52;
  const ITEM_GAP = 6;
  if (itemCount <= 0) return PADDING_Y * 2 + HEADER;
  return PADDING_Y * 2 + HEADER + HEADER_GAP + itemCount * BUTTON_MIN + (itemCount - 1) * ITEM_GAP;
}

/**
 * Center-to-center distance (px) for each consecutive pair of sections around the ring.
 * `step[i]` is the distance from section i's center to section (i+1)'s center,
 * accounting for both half-heights plus the desired edge-to-edge gap.
 */
export function sectionSteps(heights: number[], gap: number): number[] {
  const n = heights.length;
  return heights.map((h, i) => {
    const nextH = heights[(i + 1) % n] ?? h;
    return (h + nextH) / 2 + gap;
  });
}

/**
 * Continuous signed Y positions (px) for every section given the current carousel offset.
 *
 * Generalizes the uniform `itemY(relPos, spacing)` model to variable-height blocks:
 * sections are laid out along the ring by cumulative `sectionSteps`, the playhead
 * (viewport center) is interpolated from the fractional offset, and each section's
 * position is wrapped to the shortest signed path around the ring.
 *
 * Degenerates exactly to `relativePosition(i)*spacing` when all heights are equal.
 */
export function sectionYPositions(offset: number, heights: number[], gap: number): number[] {
  const n = heights.length;
  if (n === 0) return [];
  if (n === 1) return [0];

  const steps = sectionSteps(heights, gap);
  const pos: number[] = [0];
  for (let i = 1; i < n; i++) pos[i] = (pos[i - 1] ?? 0) + (steps[i - 1] ?? 0);
  const ringLength = (pos[n - 1] ?? 0) + (steps[n - 1] ?? 0);

  const norm = normalizeOffset(offset, n);
  const k = Math.floor(norm);
  const frac = norm - k;
  const playhead = (pos[k] ?? 0) + frac * (steps[k] ?? 0);

  return pos.map((p) => {
    const raw = p - playhead;
    // wrap into [-ringLength/2, ringLength/2] → shortest signed path around the ring
    return raw - ringLength * Math.round(raw / ringLength);
  });
}

/**
 * macOS Dock–style magnification scale.
 * Quadratic falloff: items near center are large, edges are small.
 *
 * @param distance  Absolute pixel distance from center
 * @param radius    Magnification radius in px (items beyond this get minScale)
 * @param minScale  Scale at edge / beyond radius
 * @param maxScale  Scale at dead center
 */
export function itemScale(
  distance: number,
  radius: number,
  minScale: number,
  maxScale: number
): number {
  const t = Math.max(0, 1 - (distance / radius) ** 2);
  return minScale + (maxScale - minScale) * t;
}

/**
 * Opacity curve — slightly sharper falloff than scale so distant items
 * fade before they shrink fully.
 */
export function itemOpacity(
  distance: number,
  radius: number,
  minOpacity: number,
  maxOpacity: number
): number {
  const t = Math.max(0, 1 - (distance / radius) ** 1.5);
  return minOpacity + (maxOpacity - minOpacity) * t;
}

/** z-index: closer items on top */
export function itemZIndex(distance: number, radius: number): number {
  return Math.round(100 * Math.max(0, 1 - distance / radius));
}

/** Check if an item should be rendered (within visible range) */
export function isItemVisible(absRelPos: number, visibleRange: number): boolean {
  return absRelPos <= visibleRange;
}

/** Snap offset to nearest integer (nearest item center) */
export function snapTarget(offset: number): number {
  return Math.round(offset);
}

/**
 * Compute the shortest-path snap target for a given index,
 * considering the current offset and wrapping around the ring.
 * E.g. if offset=5.8, index=0, count=6 → returns 6.0 (not 0).
 */
export function wrappedSnapTarget(currentOffset: number, index: number, count: number): number {
  if (count === 0) return index;
  const norm = normalizeOffset(currentOffset, count);
  // Signed shortest delta from normalized offset to target index
  const half = count / 2;
  const delta = ((((index - norm) % count) + count + half) % count) - half;
  return currentOffset + delta;
}

/** Lerp toward target, returns new value */
export function lerp(current: number, target: number, alpha: number): number {
  return current + (target - current) * alpha;
}

/** 트랙이 가장 큰 섹션보다 낮을 때 섹션 전체를 줄이는 배율. 높이를 모르면(0 이하) 줄이지 않는다. */
export const MIN_FIT_SCALE = 0.3;
export function fitScale(trackHeight: number, tallestSectionHeight: number, margin = 16): number {
  if (trackHeight <= 0 || tallestSectionHeight <= 0) return 1;
  const raw = (trackHeight - margin) / tallestSectionHeight;
  return Math.min(1, Math.max(MIN_FIT_SCALE, raw));
}
